import { useForm } from "react-hook-form";

import { Form, FormError, FormInput, FormSubmit } from "@components/forms";

import { TOTP_APP_NAME } from "@lib/authentication/two-factor";
import { ExclamationCircle } from "@svg/icons/ExclamationCircle";

import { TotpSetupDetails } from "../account/TotpSetupDetails";

import type { useLoginForm } from "./useLoginForm";

interface StepFormData {
  totpCode?: string;
  newPassword?: string;
  confirmPassword?: string;
}

// The steps after the password (OMG-2777): a new password on a first sign-in
// to the admin pool, then the authenticator setup or code. Shown by the
// sign-in form and, straight after a password reset, by the reset form.
export const SignInStepForm = ({
  loginForm,
}: {
  loginForm: ReturnType<typeof useLoginForm>;
}) => {
  const {
    submitTotp,
    submitNewPassword,
    step,
    totpSetup,
    errors,
    isSubmitting,
  } = loginForm;
  const { register, handleSubmit, setValue } = useForm<StepFormData>({
    shouldUnregister: true,
  });

  if (step === "new-password") {
    return (
      <Form onSubmit={handleSubmit(submitNewPassword)} className="w-full">
        <div className="w-full font-mabry-pro-bold text-32px tracking-tight">
          Choose a new password
        </div>
        <p className="mt-8px text-14px">
          Your account was set up with a temporary password. Choose your own: at
          least 12 characters, with upper and lower case letters, a number and a
          symbol.
        </p>
        <FormError hidden={!errors.newPassword && !errors.confirmPassword}>
          <ul>
            <li className="flex">
              <ExclamationCircle className="mr-4px inline min-h-[21px] min-w-14px" />
              {errors.newPassword?.message ?? errors.confirmPassword?.message}
            </li>
          </ul>
        </FormError>
        <div className="my-32px w-full">
          <FormInput
            className="mb-4 w-full"
            register={() => register("newPassword", { required: true })}
            label="New password"
            error={errors.newPassword}
            errorClassName={"hidden"}
            type="password"
            autoComplete="new-password"
            autoFocus
            onChange={(e) => {
              setValue("newPassword", e.target.value);
            }}
          />
          <FormInput
            className="mb-4 w-full"
            register={() => register("confirmPassword", { required: true })}
            label="New password again"
            error={errors.confirmPassword}
            errorClassName={"hidden"}
            type="password"
            autoComplete="new-password"
            onChange={(e) => {
              setValue("confirmPassword", e.target.value);
            }}
          />
        </div>
        <FormSubmit
          disabled={isSubmitting}
          value="Continue"
          className="h-[40px] !pl-16px !pr-16px sm:!pl-24px sm:!pr-24px w-full"
        />
      </Form>
    );
  }

  return (
    <Form onSubmit={handleSubmit(submitTotp)} className="w-full">
      <div className="w-full font-mabry-pro-bold text-32px tracking-tight">
        {totpSetup ? "Set up your authenticator" : "Enter your code"}
      </div>
      {totpSetup ? (
        <>
          <p className="mt-8px text-14px">
            Admin accounts need a code from an authenticator app (Google
            Authenticator, 1Password, Authy or iOS Passwords) as well as a
            password. Scan this with the app, then enter the 6-digit code it
            shows.
          </p>
          <TotpSetupDetails setup={totpSetup} />
        </>
      ) : (
        <p className="mt-8px text-14px">
          Open your authenticator app and enter the 6-digit code for{" "}
          {TOTP_APP_NAME}.
        </p>
      )}
      <FormError hidden={!errors.totpCode}>
        <ul>
          <li className="flex">
            <ExclamationCircle className="mr-4px inline min-h-[21px] min-w-14px" />
            {errors.totpCode?.message}
          </li>
        </ul>
      </FormError>
      <div className="my-32px w-full">
        <FormInput
          className="mb-4 w-full"
          register={() => register("totpCode", { required: true })}
          label="Authenticator code"
          error={errors.totpCode}
          errorClassName={"hidden"}
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
          onChange={(e) => {
            setValue("totpCode", e.target.value);
          }}
        />
      </div>
      <FormSubmit
        disabled={isSubmitting}
        value="Verify"
        className="h-[40px] !pl-16px !pr-16px sm:!pl-24px sm:!pr-24px w-full"
      />
    </Form>
  );
};
